/**
 * The size ceiling for any image a merchant uploads, in bytes.
 *
 * 1 MB counted as 1024×1024, matching how a file manager reports a file's size
 * — a merchant checking "is my photo under 1 MB?" in Finder or Explorer sees
 * the same number this compares against.
 */
export const MAX_IMAGE_BYTES = 1024 * 1024;

/** `MAX_IMAGE_BYTES` as the "1 MB" that appears in hint and error copy. */
export const MAX_IMAGE_LABEL = '1 MB';

/**
 * Split a pick into the files that fit and the names of those that do not.
 *
 * Enforced on the CLIENT at the moment of picking, which is the only place it
 * can be enforced usefully: the upload is a presigned PUT straight to object
 * storage (see `uploadApi`), so an oversized file is not rejected by our API at
 * all — it is uploaded in full, and the merchant pays for the transfer before
 * anything notices. Checking here also means the failure names the offending
 * file while the merchant is still looking at the picker.
 *
 * Oversized files are REPORTED, never silently dropped: a photo that vanishes
 * on selection reads as a broken uploader, while a named rejection tells the
 * merchant which file to shrink.
 */
export function partitionBySize(
  files: readonly File[],
  maxBytes: number = MAX_IMAGE_BYTES,
): { accepted: File[]; rejected: string[] } {
  const accepted: File[] = [];
  const rejected: string[] = [];
  for (const file of files) {
    if (file.size > maxBytes) rejected.push(file.name);
    else accepted.push(file);
  }
  return { accepted, rejected };
}
